import _ from 'lodash'

/**
 * Configures TMR Resource administration pages
 *
 * Create a list route with "resourceType" option and it will take care to
 * automatically create all administration pages. You can override specific
 * route/state options by adding a route with the right name with only the
 * options that you want to override.
 *
 * @todo Override example
 *
 * The UI Router state tree that results is as follows:
 *
 *   app.[...] (abstract base state, empty but useful to jump between the list and the view branches)
 *   app.[...].list (defined by user; all other states are generated from this)
 *   app.[...].view
 *   app.[...].view.edit
 *   app.[...].view.delete
 *   app.[...].add
 *
 * @alias TmrAdminResourceRoutesProvider
 * @type {ngProvider}
 */
export class TmrAdminResourceRoutes {
  static ngType = 'provider'

  constructor() {
    'ngInject'
    this.routes = {}
  }

  $get($state) {
    'ngInject'
    return new TmrAdminResourceRoutesService(this, $state)
  }

  /**
   * Returns true if the route ends with `.list` and has a `resourceType`.
   */
  isResourceAdminRoute({ name, resourceType }) {
    return name.endsWith('.list') && resourceType
  }

  baseRouteName(listRouteName) {
    return listRouteName.replace(/\.list$/, '')
  }

  addRouteName(listRouteName) {
    return listRouteName.replace(/\.list$/, '.add')
  }

  viewRouteName(listRouteName) {
    return listRouteName.replace(/\.list$/, '.view')
  }

  editRouteName(listRouteName) {
    return this.viewRouteName(listRouteName) + '.edit'
  }

  deleteRouteName(listRouteName) {
    return this.viewRouteName(listRouteName) + '.delete'
  }

  /**
   * Adds all administration routes for all resource types.
   */
  setupRoutes(routes) {
    this.baseRoutes(routes)
    this.addRoutes(routes)
    this.viewRoutes(routes)
    this.editRoutes(routes)
    this.deleteRoutes(routes)
  }

  /**
   * Adds base routes to administration sections that have the "resourceType"
   * option set and a valid route name.
   *
   * The base route is used only to group the routes of the same resource type
   * together, so we can setup breadcrumbs and other relative navigation without
   * further configuration.
   *
   */
  baseRoutes(routes) {
    routes
      .filter(route => this.isResourceAdminRoute(route))
      .forEach(route => {
        this.routes[route.resourceType] = { list: route.name }
        this.defaultRoute(routes, this.baseRoute(route))
      })
  }

  /**
   * Builds an abstract resource base route
   *
   * Base route name format: app.resource.[resourceType]
   *
   * @example
   * resourceType = User
   * listRouteName = app.admin.users.list
   *   => name = app.admin.users
   *
   * @todo Apply the list url to base url and swap it with '/?'
   */
  baseRoute(route) {
    this.routes[route.resourceType].base = this.baseRouteName(route.name)
    return {
      name: this.routes[route.resourceType].base,
      abstract: true,
    }
  }

  /**
   * Adds add routes to administration sections that have the "resourceType"
   * option set.
   */
  addRoutes(routes) {
    routes
      .filter(r => this.isResourceAdminRoute(r))
      .forEach(r => this.defaultRoute(routes, this.addRoute(r)))
  }

  /**
   * Builds a resource add route
   *
   * Add route name format: app.resources.[resourceType].add
   * Translation key format: ADMIN.[PLURAL].ADD.*
   *
   * @example
   * resourceType = 'User'
   * listRouteName = app.admin.users.list
   *   => name = app.admin.users.add
   *   => url = [list url]/add
   */
  addRoute(route) {
    this.routes[route.resourceType].add = this.addRouteName(route.name)
    let labelPrefix = `${route.label.split('.')[0]}.${this.getTranslationKey(
      route
    )}.ADD`
    let url = new URL(route.url, 'https://foo.bar')
    return {
      name: this.routes[route.resourceType].add,
      label: `${labelPrefix}.TITLE`,
      url: `${url.pathname}/add`,
      component: `tmrAdmin${route.resourceType}Form`,
      accessControl: ['Resource.edit', `Resource.${route.resourceType}.edit`],
      params: {
        op: 'add',
        duplicateResource: null,
      },
      resolve: {
        resource: [
          route.resourceType,
          '$transition$',
          (TmrResource, $transition$) => {
            const { duplicateResource } = $transition$.params()
            if (duplicateResource) {
              duplicateResource.id = null
              duplicateResource.code = null
            }
            return duplicateResource ?? TmrResource.create()
          },
        ],
        toast: () => `${labelPrefix}.ADDED`,
      },
    }
  }

  /**
   * Adds view routes to administration sections that have the "resourceType"
   * option set.
   */
  viewRoutes(routes) {
    routes
      .filter(r => this.isResourceAdminRoute(r))
      .forEach(r => this.defaultRoute(routes, this.viewRoute(r)))
  }

  /**
   * Builds a resource view route
   *
   * Abstract by default since most resources don't need a view page, but edit
   * and delete inherit from this so it must exist.
   *
   * @example
   * resourceType = 'User'
   * listRouteName = app.admin.users.list
   *   => name = app.admin.users.view
   *   => url = [list url]/:id
   */
  viewRoute(route) {
    this.routes[route.resourceType].view = this.viewRouteName(route.name)
    let url = new URL(route.url, 'https://foo.bar')
    return {
      name: this.routes[route.resourceType].view,
      url: `${url.pathname}/:id`,
      abstract: true,
      accessControl: ['Resource.list', `Resource.${route.resourceType}.list`],
      resolve: {
        resource: [
          route.resourceType,
          '$transition$',
          (TmrResource, $transition$) =>
            TmrResource.get({ id: $transition$.params().id }).$promise,
        ],
      },
    }
  }

  /**
   * Adds edit routes to administration sections that have the "resourceType"
   * option set.
   */
  editRoutes(routes) {
    routes
      .filter(r => this.isResourceAdminRoute(r))
      .forEach(r => this.defaultRoute(routes, this.editRoute(r)))
  }

  /**
   * Builds a resource edit route
   *
   * Translation key format: ADMIN.[PLURAL].EDIT.*
   *
   * @example
   * resourceType = 'User'
   * listRouteName = app.admin.users.list
   *   => name = app.admin.users.view.edit
   *   => url = [list url]/:id/edit
   */
  editRoute(route) {
    this.routes[route.resourceType].edit = this.editRouteName(route.name)
    let labelPrefix = `${route.label.split('.')[0]}.${this.getTranslationKey(
      route
    )}.EDIT`
    return {
      name: this.routes[route.resourceType].edit,
      label: `${labelPrefix}.TITLE`,
      url: '/edit',
      component: `tmrAdmin${route.resourceType}Form`,
      accessControl: ['Resource.edit', `Resource.${route.resourceType}.edit`],
      params: {
        op: 'edit',
      },
      resolve: {
        toast: () => `${labelPrefix}.UPDATED`,
      },
    }
  }

  /**
   * Adds delete routes to administration sections that have the "resourceType"
   * option set.
   */
  deleteRoutes(routes) {
    routes
      .filter(r => this.isResourceAdminRoute(r))
      .forEach(r => this.defaultRoute(routes, this.deleteRoute(r)))
  }

  /**
   * Builds a resource delete route
   *
   * Delete route name format: app.resources.[resourceType].view.delete
   * Translation key format: ADMIN.[PLURAL].DELETE.*
   *
   * @example
   * resourceType = 'User'
   * listRouteName = app.admin.users.list
   *   => name = app.admin.users.view.delete
   *   => url = [list url]/:id/delete
   */
  deleteRoute(route) {
    this.routes[route.resourceType].delete = this.deleteRouteName(route.name)
    let labelPrefix = `${route.label.split('.')[0]}.${this.getTranslationKey(
      route
    )}.DELETE`
    return {
      name: this.routes[route.resourceType].delete,
      label: `${labelPrefix}.TITLE`,
      url: '/delete',
      component: 'tmrResourceDelete',
      params: {
        redirect: null,
      },
      resolve: {
        question: () => `${labelPrefix}.CONFIRM`,
        toast: () => `${labelPrefix}.DELETED`,
        canDelete: /*@ngInject*/ resource => resource.$canDelete(),
      },
      accessControl: ['Resource.edit', `Resource.${route.resourceType}.edit`],
    }
  }

  /**
   * Adds defaultRoute to routes, eventually merging it with an existing route
   * with the same name.
   *
   * @param {array} routes
   * @param {object} defaultRoute
   */
  defaultRoute(routes, defaultRoute) {
    let existingRouteIdx = routes.findIndex(
      ({ name }) => name == defaultRoute.name
    )
    if (existingRouteIdx == -1) routes.push(defaultRoute)
    else
      routes[existingRouteIdx] = _.merge(defaultRoute, routes[existingRouteIdx])
  }

  /**
   * Returns the resource part of the translation key of a route.
   *
   * @example
   * resourceType = 'User'
   *   => key = 'USERS'
   */
  getTranslationKey(route) {
    return route.label.split('.')[1]
  }
}

/**
 * Utility to build resource route names, useful for `ui-sref` links and `$state.go()`
 *
 * @alias TmrAdminResourceRoutes
 * @type {ngService}
 */
class TmrAdminResourceRoutesService {
  constructor(TmrAdminResourceRoutesProvider, $state) {
    Object.assign(this, {
      routes: TmrAdminResourceRoutesProvider.routes,
      $state,
    })
    for (let resourceType in this.routes) {
      for (let op in this.routes[resourceType]) {
        if (!this.routes[resourceType][op].startsWith('app.')) {
          this.routes[resourceType][op] = 'app.' + this.routes[resourceType][op]
        }
      }
    }
  }

  /** @param {string} resourceType */
  baseRouteName(resourceType) {
    return this.routes[resourceType].base
  }

  /** @param {string} resourceType */
  listRouteName(resourceType) {
    return this.routes[resourceType].list
  }

  /** @param {string} resourceType */
  addRouteName(resourceType) {
    return this.routes[resourceType].add
  }

  /** @param {string} resourceType */
  viewRouteName(resourceType) {
    return this.routes[resourceType].view
  }

  /** @param {string} resourceType */
  editRouteName(resourceType) {
    return this.routes[resourceType].edit
  }

  /** @param {string} resourceType */
  deleteRouteName(resourceType) {
    return this.routes[resourceType].delete
  }

  /**
   * Retrieves a route definition from the router
   *
   * @param {string} resourceType
   * @param {'base'|'list'|'add'|'view'|'edit'|'delete'} routeType
   */
  getRoute(resourceType, routeType) {
    return this.routes[resourceType]
      ? this.$state.get(this.routes[resourceType][routeType])
      : undefined
  }
}
